import type { LifecycleKind } from "./lifecycleState";

export interface LifecycleCopyEntry {
  /** Short label for badge / chip (e.g. "Live") */
  badge: string;
  /** Full status line with version placeholder ({v}) */
  withVersion: string;
  /** Tooltip shown on hover */
  tooltip: string;
  /** Dot color key consumed by the badge/chip */
  tone: "green" | "amber" | "purple" | "blue" | "gray";
}

export const LIFECYCLE_COPY: Record<LifecycleKind, LifecycleCopyEntry> = {
  unpublished: {
    badge: "Unpublished",
    withVersion: "Unpublished",
    tooltip: "This workflow has never been published. Publish to make it run.",
    tone: "purple",
  },
  live: {
    badge: "Live",
    withVersion: "Live · v{v}",
    tooltip: "Published and accepting triggers.",
    tone: "green",
  },
  "live-edits": {
    badge: "Live",
    withVersion: "Live · v{v}",
    tooltip: "Live is running v{v}. You have unpublished edits.",
    tone: "green",
  },
  paused: {
    badge: "Paused",
    withVersion: "Paused · v{v}",
    tooltip: "Published but triggers are off. Resume to start firing again.",
    tone: "amber",
  },
  "paused-edits": {
    badge: "Paused",
    withVersion: "Paused · v{v}",
    tooltip: "Paused on v{v}. You have unpublished edits.",
    tone: "amber",
  },
  archived: {
    badge: "Archived",
    withVersion: "Archived",
    tooltip: "Soft-deleted. Un-archive from the Archived tab to restore.",
    tone: "gray",
  },
};

export function formatLifecycleBadge(kind: LifecycleKind, version: number | null): string {
  const entry = LIFECYCLE_COPY[kind];
  if (version == null) return entry.badge;
  return entry.withVersion.replace("{v}", String(version));
}

export function formatEditsLabel(count: number | null): string {
  if (count == null) return "Unpublished edits";
  return `${count} ${count === 1 ? "edit" : "edits"}`;
}

/** Shared Tailwind class maps for the lifecycle tones — single source of
 * truth consumed by both WorkflowStatusBadge and WorkflowStatusChip so the
 * two surfaces cannot drift. Uses design-system tokens (ds-*) where
 * available; the purple "unpublished" tone has no ds-* equivalent yet and
 * falls back to neutral Tailwind with a named token request pending. */
export const LIFECYCLE_TONE_BG: Record<LifecycleCopyEntry["tone"], string> = {
  green: "bg-ds-success-surface text-ds-success-text border-ds-success-border",
  amber: "bg-ds-warning-surface text-ds-warning-text border-ds-warning-border",
  purple: "bg-purple-500/15 text-purple-300 border-purple-500/40",
  blue: "bg-ds-info-surface text-ds-info-text border-ds-info-border",
  gray: "bg-ds-surface-muted text-ds-text-subtle border-ds-border-default",
};

export const LIFECYCLE_TONE_DOT: Record<LifecycleCopyEntry["tone"], string> = {
  green: "bg-ds-success-accent",
  amber: "bg-amber-800 dark:bg-amber-300",
  purple: "bg-purple-300",
  blue: "bg-ds-info-text",
  gray: "bg-ds-text-disabled",
};

/** Button / action labels — keep in one place so we don't drift. */
export const LIFECYCLE_LABELS = {
  publish: "Publish",
  publishVersion: (v: number) => `Publish v${v}`,
  publishStaysPaused: (v: number) => `Publish v${v} (stays paused)`,
  discardEdits: "Discard edits",
  revert: (v: number) => `Revert to v${v}`,
  restore: "Restore",
  pause: "Pause",
  resume: "Resume",
  archive: "Archive workflow",
  unarchive: "Un-archive",
  deletePermanently: "Delete permanently",
  test: "Test",
  run: "Run",
  triggersOn: "On",
  triggersOff: "Off",
} as const;
